import { useEffect, useState, useRef, type ComponentType } from 'react';

import { useLocation } from 'react-router-dom';

import { denyAnalyticsConsent, getConsentReceipt, getStoredConsent, hasGlobalPrivacyControl, onConsentEvent } from '../lib/analytics/consent';
import { disarmGtm, isArmed } from '../lib/analytics/gtm';
import { dismissMeasurementNotice, initializePrivacyNotice, needsMeasurementNotice, recordVisibleNotice, restoreAnalytics, revokeMeasurement, revokeGoogleMeasurement } from '../lib/analytics/measurement';
import { MEASUREMENT_NOTICE } from '../lib/analytics/measurementContract';
import { isAnalyticsEligible } from '../lib/analytics/publicRoutes';
import { importOptional } from '../utils/vitePreloadRecovery';

import type { ConsentNoticeProps } from './ConsentNotice';

const FALLBACK_CHOICE = 'min-h-11 flex-1 rounded-lg border px-2 text-sm font-semibold';

// Keep disclosure and withdrawal in the shell even when optional settings fail
// to load. The native dialog handles background inertness and keyboard focus.
export function ConsentBanner() {
  const location = useLocation();
  const publicRoute = isAnalyticsEligible(location.pathname);
  const privateRateRoute = location.pathname === '/rate-experience' || location.pathname.startsWith('/rate-quote/');
  const choiceRevision = useRef(0);
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [googleMessage, setGoogleMessage] = useState('');
  const [denied, setDenied] = useState(getStoredConsent() === 'denied' || hasGlobalPrivacyControl());
  const [requiresOptIn, setRequiresOptIn] = useState(true);
  const [Notice, setNotice] = useState<ComponentType<ConsentNoticeProps> | null>(null);

  useEffect(() => {
    let active = true;
    const show = () => {
      if (active && isAnalyticsEligible(window.location.pathname) && getStoredConsent() !== 'denied' && !hasGlobalPrivacyControl() && needsMeasurementNotice()) setVisible(true);
    };
    void initializePrivacyNotice(() => active, context => {
      setRequiresOptIn(!context?.analyticsDefault);
      if (context?.denied) setDenied(true);
    }, show).catch(show);
    const unsubscribe = onConsentEvent(event => {
      if (event.type === 'choices_requested') { setCustomizing(true); setVisible(true); return; }
      if (event.decision === 'denied') choiceRevision.current++;
      setDenied(event.decision === 'denied');
      if (event.decision === 'denied') { if (isArmed()) disarmGtm(); setVisible(false); }
    });
    return () => { active = false; unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!visible) return;
    let active = true;
    // A frame with the notice in the DOM precedes optional collection.
    const frame = requestAnimationFrame(() => {
      if (publicRoute) {
        void recordVisibleNotice(() => active);
      }
    });
    if (!Notice) void importOptional(() => import('./ConsentNotice')).then(module => {
      if (active && typeof module?.ConsentNotice === 'function') setNotice(() => module.ConsentNotice);
    });
    return () => { active = false; cancelAnimationFrame(frame); };
  }, [visible, publicRoute, Notice]);

  const optOut = async () => {
    const withdrawing = isArmed();
    const saved = denyAnalyticsConsent();
    disarmGtm(); setDenied(true);
    const acknowledged = await revokeMeasurement();
    if (!saved || !acknowledged) { setUnsaved(true); setVisible(true); return; }
    setUnsaved(false); setVisible(false);
    if (withdrawing) window.location.replace(window.location.pathname);
  };
  const allow = async () => {
    const choice = choiceRevision.current, receipt = getConsentReceipt();
    if (!await restoreAnalytics(() => choice === choiceRevision.current, receipt)) {
      setUnsaved(true); setVisible(true); return;
    }
    setDenied(false); setVisible(false);
  };
  const close = () => { dismissMeasurementNotice(); setVisible(false); setCustomizing(false); };
  if (!publicRoute && !privateRateRoute) return null;
  if (!visible) return privateRateRoute ? <button type="button" style={{ position: 'fixed' }} className="fixed bottom-2 right-3 z-40 text-xs underline print:hidden" onClick={() => { setCustomizing(true); setVisible(true); }}>Privacy choices</button> : null;
  const gpc = hasGlobalPrivacyControl();
  if (Notice) return <Notice denied={denied} gpc={gpc} requiresOptIn={requiresOptIn} unsaved={unsaved}
    detailsRequested={customizing} onOptOut={() => { void optOut(); }} onAllow={allow} onClose={close} />;
  return <section role="dialog" aria-label="Privacy choices" aria-modal="false" aria-describedby="privacy-notice-description"
    style={{ position: 'fixed' }} className="fixed bottom-4 left-4 right-4 z-[90] max-w-lg rounded-2xl border border-gray-200 bg-white p-6 font-sans text-brand-ink shadow-2xl print:hidden">
    <h2 className="mb-3 font-sans text-xl font-semibold">Your privacy matters</h2>
    <p id="privacy-notice-description" className="text-sm leading-relaxed">{denied ? 'Optional measurement is off.' : MEASUREMENT_NOTICE}{' '}
      <a href="/privacy-policy#analytics" className="underline">Privacy policy</a></p>
    {unsaved && <p role="status" className="mt-3 text-sm">We could not confirm that your choice was saved. Measurement stays off here.</p>}
    <div className="mt-6 flex gap-2">
      <button type="button" className={FALLBACK_CHOICE} onClick={() => { void optOut(); }}>Reject optional</button>
      <button type="button" className={FALLBACK_CHOICE} onClick={close}>Continue</button>
    </div>
    <button type="button" className="mt-3 text-xs underline" onClick={() => { setGoogleMessage('Saving…'); void revokeGoogleMeasurement().then(saved => setGoogleMessage(saved ? 'Saved.' : 'Please retry.')); }}>Stop sharing with Google</button>
    <p role="status" className="text-xs">{googleMessage}</p>
  </section>;
}
